import { FC } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { HelpTag } from "~/core/slices/help/tags";
import { useApp } from "~/core/store";
import { Button } from "~/components";
import { DiscoveryDetailsRenderer } from "~/organisms/DiscoveryDetailsRenderer";
import ContentEditorFooter from "../ContentEditorFooter";
import { getHistoryLabel } from "../utils";
import Suggestions from "./Suggestions";
import { TabProps } from "./interfaces";
import PencilIcon from "@material-design-icons/svg/outlined/create.svg";
import PictureIcon from "@material-design-icons/svg/outlined/image.svg";
import PreSendIcon from "@material-design-icons/svg/outlined/preview.svg";
import SaveIcon from "@material-design-icons/svg/outlined/save.svg";
import SendIcon from "@material-design-icons/svg/outlined/send.svg";
import Loader from "~/assets/loader.svg";
import cn from "classnames";

const Details: FC<TabProps> = ({ tag, locale }) => {
    const {
        api: { discovery },
        snack,
        help,
    } = useApp();
    const infoLoading = discovery.loading(tag.tag);
    const infoLoaded = discovery.loaded(tag.tag);
    const historyLoading = discovery.historyLoading(tag.tag);
    const historyLoaded = discovery.historyLoaded(tag.tag);
    const actions = help.admin.actions(tag.tag);
    const state = actions.state;
    const saving = useSignal(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const padRef = useRef<HTMLDivElement>(null);
    const suggest = useSignal({
        active: false,
        content: "",
        top: 0,
        left: 0,
        openIndex: 0,
    });
    const history = discovery.getHistory(tag.tag);
    const loading = infoLoading.value || historyLoading.value;
    const loaded = infoLoaded.value && historyLoaded.value;
    const currentRecord = history.value.find((i) =>
        state.detailedId === 0 ? i.active : i.detailedId === state.detailedId,
    );
    const activeRecord = history.value.find((i) => i.active);
    const prepublishContent = help.admin.getPrepublishContent(tag.tag);
    const isPrepublish =
        prepublishContent && prepublishContent?.detailedId === state.detailedId;

    void discovery.getByTag(tag.tag);

    useEffect(() => {
        const pad = padRef.current;
        const textarea = textareaRef.current;

        if (!pad || !textarea) {
            return;
        }

        const cleanSuggestions = () => {
            suggest.value = {
                active: false,
                content: "",
                top: 0,
                left: 0,
                openIndex: 0,
            };
        };

        const getLinkContent = (text: string, cursorIndex: number) => {
            const startIndex = text.lastIndexOf("](", cursorIndex);

            if (startIndex === -1) {
                return false;
            }

            const bracketBefore = text.lastIndexOf("[", startIndex);

            if (bracketBefore === -1 || bracketBefore > startIndex) {
                return false;
            }

            const openIndex = startIndex + 2;

            const cbi = text.indexOf(")", openIndex);
            const csi = text.indexOf(" ", openIndex);
            const cni = text.indexOf("\n", openIndex);
            const closeIndex = Math.min(
                cbi === -1 ? Infinity : cbi,
                csi === -1 ? Infinity : csi,
                cni === -1 ? Infinity : cni,
                text.length,
            );

            if (cursorIndex < openIndex || cursorIndex > closeIndex) {
                return false;
            }

            const content = text.slice(openIndex, closeIndex);

            return { openIndex, closeIndex, content };
        };

        const onCursorChange = () => {
            const { selectionStart, selectionEnd, value } = textarea;

            if (selectionStart !== selectionEnd) {
                cleanSuggestions();
                return;
            }

            const result = getLinkContent(value, selectionStart);

            if (!result) {
                cleanSuggestions();
                return;
            }

            const beforeCursor = value.substring(0, selectionStart);

            pad.innerHTML = `${beforeCursor.slice(0, -1)}<span>${beforeCursor.charAt(-1)}</span>`;
            pad.style.transform = `translateY(${-textarea.scrollTop}px)`;

            const rect = pad.querySelector("span")?.getBoundingClientRect();

            if (!rect) {
                cleanSuggestions();
                return;
            }

            suggest.value = {
                active: true,
                content: result.content,
                top: rect.top,
                left: rect.left,
                openIndex: result.openIndex,
            };
        };

        textarea.addEventListener("click", onCursorChange);
        textarea.addEventListener("keyup", onCursorChange);

        return () => {
            textarea.removeEventListener("click", onCursorChange);
            textarea.removeEventListener("keyup", onCursorChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectSuggestion = (value: string) => {
        const s = suggest.peek();
        const t = textareaRef.current;

        if (!t) {
            return;
        }

        const v = t.value;
        const b = v.slice(0, s.openIndex);
        const a = v.at(s.openIndex + s.content.length) === ")" ? "" : ")";
        const ni = s.openIndex + value.length + a.length;

        help.admin.edit(
            tag.tag as HelpTag,
            `${b}${value}${a}${v.slice(s.openIndex + s.content.length)}`,
        );
        t.setSelectionRange(ni, ni);
        t.focus();
    };

    const handleSave = async () => {
        saving.value = true;

        try {
            await help.admin.save(tag.tag, locale);
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : "An error occurred";
            snack.error("Error occurred", message);
        }

        saving.value = false;
    };

    const handlePrepublish = () => {
        help.admin.prepublish(tag.tag as HelpTag, !isPrepublish);
    };

    return (
        <>
            <div className="overflow-y-overlay flex h-full p-4">
                <div className="flex w-1/2 flex-col">
                    <h3 className="mb-2 flex items-center gap-x-2 text-gray-500">
                        {loading ? (
                            <Loader width="24" height="24" className="mr-2" />
                        ) : (
                            <PencilIcon
                                width="24"
                                height="24"
                                className="fill-current"
                            />
                        )}
                        <span>Content</span>
                        {currentRecord && (
                            <span
                                className={cn(
                                    currentRecord.active && "text-green-500",
                                )}
                            >
                                {getHistoryLabel(currentRecord)}
                            </span>
                        )}
                        {currentRecord?.active ? (
                            <span
                                title="Active revision"
                                className="size-2 flex-shrink-0 bg-green-600"
                            />
                        ) : activeRecord ? (
                            <span>
                                (<span>active: </span>
                                <span className="text-green-500">
                                    {getHistoryLabel(activeRecord)}
                                </span>
                                )
                            </span>
                        ) : null}
                    </h3>
                    <div className="relative h-full w-full">
                        <div className="absolute inset-0 rounded-lg border p-2 opacity-0">
                            <div
                                ref={padRef}
                                className="whitespace-pre-wrap break-words"
                            />
                        </div>
                        <textarea
                            ref={textareaRef}
                            data-content-editor
                            value={state.content}
                            onInput={({ currentTarget }) => {
                                const newValue = currentTarget.value;

                                help.admin.edit(tag.tag as HelpTag, newValue);
                            }}
                            className="relative z-10 h-full w-full resize-none rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-white"
                            placeholder="Enter content here..."
                        />
                        <Suggestions
                            suggest={suggest}
                            onSelect={handleSelectSuggestion}
                        />
                    </div>
                </div>
                <div className="ml-2 flex w-1/2 flex-col">
                    <h3 className="mb-2 flex items-center text-gray-500">
                        <PictureIcon
                            width="24"
                            height="24"
                            className="mr-2 fill-current"
                        />
                        <span>Preview</span>
                    </h3>
                    <div className="h-full rounded-lg border border-gray-300 p-2">
                        <DiscoveryDetailsRenderer details={state.content} />
                    </div>
                </div>
            </div>
            <ContentEditorFooter disabled={saving.value} loading={saving.value}>
                {loading && !loaded ? (
                    <Loader width="20" height="20" className="text-sky-500" />
                ) : (
                    <>
                        <Button
                            disabled={saving.value || !actions.create}
                            className="gap-x-2"
                            onClick={handleSave}
                        >
                            {saving.value ? (
                                <Loader width="18" height="18" />
                            ) : (
                                <SaveIcon width="18" height="18" />
                            )}
                            <span>
                                {actions.init ? "Save and publish" : "Save"}
                            </span>
                        </Button>
                        <Button
                            disabled={saving.value || !actions.publish}
                            className={cn("gap-x-2", actions.init && "!hidden")}
                            onClick={handleSave}
                        >
                            {saving.value ? (
                                <Loader width="18" height="18" />
                            ) : (
                                <SendIcon width="18" height="18" />
                            )}
                            <span>Publish</span>
                        </Button>
                        <Button
                            title="See how it looks like. It has effect on this device only and will be removed after reloading the page."
                            disabled={saving.value || !actions.publish}
                            className={cn(
                                "gap-x-2",
                                isPrepublish && "!text-sky-700",
                                actions.init && "!hidden",
                            )}
                            onClick={handlePrepublish}
                        >
                            <PreSendIcon
                                width="18"
                                height="18"
                                className={cn(isPrepublish && "fill-sky-700")}
                            />
                            <span>
                                {isPrepublish
                                    ? "Stop pre-publish"
                                    : "Pre-publish"}
                            </span>
                        </Button>
                    </>
                )}
            </ContentEditorFooter>
        </>
    );
};

export default Details;
